import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Checkbox } from "@/components/elements/Checkbox";
import { CityInput } from "@/components/elements/CityInput";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { useGetNationalityList } from "@/api/services/global/master";
import { TPartnerForm } from "@/features/talent/types/partnerForm";
import { JAPAN_NATIONALITY_ID } from "@/types/enum";
import { divider, text } from "@/theme/colors";

type TPartnerBasicInfoFormProps = {
  readonly?: boolean;
};

export const PartnerBasicInfoForm: FC<TPartnerBasicInfoFormProps> = ({ readonly }) => {
  const methods = useFormContext<TPartnerForm>();
  const { control, setValue, formState } = methods;
  const { nationalityOptions } = useGetNationalityList();
  const sendingOrganization = useWatch({ control, name: "sendingOrganization" });
  const laborBureauStatus = useWatch({ control, name: "laborBureauStatus" });
  const nationalityId = useWatch({ control, name: "nationalityId" });

  const isJapanSelected = useMemo(() => {
    return Number(nationalityId) === JAPAN_NATIONALITY_ID;
  }, [nationalityId]);

  const filteredNationalityOptions = useMemo(() => {
    if (sendingOrganization === true) {
      return nationalityOptions.filter((option) => Number(option.value) !== JAPAN_NATIONALITY_ID);
    }
    return nationalityOptions;
  }, [nationalityOptions, sendingOrganization]);

  const prevSendingOrganizationRef = useRef<boolean | undefined | null>(undefined);
  useEffect(() => {
    prevSendingOrganizationRef.current = sendingOrganization;
  }, []);

  useEffect(() => {
    if (sendingOrganization === false && prevSendingOrganizationRef.current === true) {
      const currentNationalityId = methods.getValues("nationalityId");
      if (currentNationalityId === undefined || currentNationalityId === null) {
        setValue("nationalityId", JAPAN_NATIONALITY_ID, { shouldValidate: true });
      }
    }

    if (sendingOrganization === true && prevSendingOrganizationRef.current === false) {
      const defaultNationalityId = formState.defaultValues?.nationalityId;
      if (defaultNationalityId) {
        setValue("nationalityId", Number(defaultNationalityId), { shouldValidate: true });
      }
    }

    prevSendingOrganizationRef.current = sendingOrganization;
  }, [sendingOrganization, setValue, methods, formState.defaultValues]);

  const locationInfo = useLocationInfo({
    methods,
    names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" },
  });

  return (
    <InnerBox title={<Typography variant="sub16Bold">基本情報</Typography>} showDivider={false} borderColor={divider.high} sx={{ gap: "24px" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormField control={control} name="managementNumber">
            <Input readonly={readonly} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="partnerName">
            <Input readonly={readonly} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <Stack gap={0.5}>
            <Typography variant="cap12" color={text.primary}>
              項目名
            </Typography>
            <Stack direction="row" alignItems="center" gap={3}>
              <FormField control={control} name="sendingOrganization" noLabel>
                <Checkbox label="送り出し機関" checked={!!sendingOrganization} disabled={readonly} />
              </FormField>
              <FormField control={control} name="laborBureauStatus" noLabel>
                <Checkbox label="労働局届出済" checked={!!laborBureauStatus} disabled={readonly} />
              </FormField>
            </Stack>
            <Typography variant="cap12" color={text.secondary}>
              送り出し機関情報
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <FormField control={control} name="nationalityId">
            <Select key={`nationality-select-${sendingOrganization}`} options={filteredNationalityOptions} readonly={readonly} />
          </FormField>
        </Grid>

        {isJapanSelected && (
          <>
            <Grid item xs={4}>
              <FormField control={control} name="zipCode" isMessageNowrap>
                <Input readonly={readonly} />
              </FormField>
            </Grid>
            <Grid item xs={4}>
              <FormField control={control} name="prefecture">
                <Input readonly />
              </FormField>
            </Grid>
            <Grid item xs={4}>
              <CityInput name="city" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} />
            </Grid>
            <Grid item xs={12}>
              <FormField control={control} name="address">
                <Input readonly={readonly} />
              </FormField>
            </Grid>
          </>
        )}

        {nationalityId && !isJapanSelected && (
          <Grid item xs={12}>
            <FormField control={control} name="location">
              <Input readonly={readonly} />
            </FormField>
          </Grid>
        )}
      </Grid>
    </InnerBox>
  );
};
